import React, { useRef, useState } from "react";
import { Box, Typography, ClickAwayListener, SxProps } from "@mui/material";
import { useTheme } from "@mui/material";
import AcxChip from "./AcxChip";
import AcxMainTextField from "./AcxMainTextField";

interface AcxInputWithChipsProps {
    values: string[];
    setValues: (values: string[]) => void;
    placeholderText?: string;
    sx?: SxProps;
}

const AcxInputWithChips: React.FC<AcxInputWithChipsProps> = ({
    values,
    setValues,
    placeholderText,
    sx,
}) => {
    const theme = useTheme();
    const [currentWords, setCurrentWords] = useState("");
    const [focusedChipIndex, setFocusedChipIndex] = useState(-1);
    const textBoxRef = useRef<HTMLInputElement>(null);

    return (
        <ClickAwayListener onClickAway={() => setFocusedChipIndex(-1)}>
            <Box
                onClick={() => {
                    if (textBoxRef.current) {
                        textBoxRef.current.focus();
                    }
                }}
                sx={{
                    position: "relative",
                    border: `solid 1px ${theme.palette.gray[200]}`,
                    padding: theme.spacing(1),
                    borderRadius: 1,
                    overflow: "auto",
                    input: {
                        padding: theme.spacing(0.5, 0, 0),
                    },
                    ".MuiInputBase-root": {
                        padding: 0,
                    },
                    ...sx,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",

                        gap: 1,
                    }}
                >
                    {values?.map((words, index) => (
                        <AcxChip
                            key={index}
                            label={words}
                            onDelete={() => {
                                setValues(values.filter((w) => w !== words));
                                setFocusedChipIndex(-1);
                            }}
                            size="small"
                            color="lightBlue"
                            onClick={() => setFocusedChipIndex(index)}
                            sx={{
                                ...(focusedChipIndex === index && {
                                    backgroundColor: theme.palette.gray[300],
                                }),
                            }}
                        />
                    ))}
                    <AcxMainTextField
                        ref={textBoxRef}
                        textContainerSx={{
                            border: 0,
                            padding: 0,
                            minHeight: "unset",
                        }}
                        containerStyle={{
                            flex: "1 0 150px",
                        }}
                        id="hot-words"
                        value={currentWords}
                        onFocus={() => setFocusedChipIndex(-1)}
                        onChange={(e) => {
                            setCurrentWords(e.target.value);
                            setFocusedChipIndex(-1);
                        }}
                        sx={{ padding: 0 }}
                        placeholderText={placeholderText}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                if (currentWords) {
                                    setValues(values.concat(currentWords));
                                    setCurrentWords("");
                                }
                            }
                            if (e.key === "Backspace") {
                                if (!currentWords) {
                                    if (
                                        focusedChipIndex === -1 &&
                                        values?.length
                                    ) {
                                        setFocusedChipIndex(values.length - 1);
                                    } else if (focusedChipIndex >= 0) {
                                        setValues(
                                            values.filter(
                                                (_, i) =>
                                                    i !== focusedChipIndex,
                                            ),
                                        );
                                        setFocusedChipIndex(-1);
                                    }
                                }
                            }
                        }}
                        fullWidth
                    />
                </Box>

                <Typography
                    sx={{
                        position: "relative",
                        width: "100%",
                        textAlign: "right",
                        color: theme.palette.gray.main,
                    }}
                >
                    Press ↵ to add words or phrases
                </Typography>
            </Box>
        </ClickAwayListener>
    );
};

export default AcxInputWithChips;
